import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography,
} from '@mui/material';

import defaultClient from '@src/api/clients/default';
import { toast } from '@src/components/core/toaster';
import PinnableMedia, { fmtTime, pinsFromComments } from '@src/components/creative/PinnableMedia';

const CTAS = [
  ['LEARN_MORE', 'Learn More'], ['SIGN_UP', 'Sign Up'], ['GET_QUOTE', 'Get Quote'], ['CONTACT_US', 'Contact Us'],
  ['BOOK_NOW', 'Book Now'], ['CALL_NOW', 'Call Now'], ['GET_OFFER', 'Get Offer'],
];

const PIECE_TRIES = 3;

/**
 * Send one creative file to storage in ~5 MB pieces. A whole video in one
 * request can outlast the proxy's 60-second timeout on a normal office
 * connection; a piece takes seconds. Resolves to a file token for the upload
 * and Replace endpoints, or to null when the server has no S3 (local dev) —
 * then send the File itself as before. onProgress gets a 0–1 fraction.
 */
export async function uploadCreativeFile(clientSlug, file, onProgress = () => {}) {
  const base = `app1/creative-review/${clientSlug}/upload-pieces`;
  const start = await defaultClient.request({
    url: `${base}/start/`, method: 'POST', payload: { filename: file.name, content_type: file.type || '' },
  });
  if (start.data?.direct) return null;
  const { upload, piece_size: pieceSize } = start.data;
  const pieces = Math.max(1, Math.ceil(file.size / pieceSize));
  try {
    for (let n = 1; n <= pieces; n += 1) {
      const piece = file.slice((n - 1) * pieceSize, n * pieceSize);
      const sentBefore = (n - 1) * pieceSize;
      for (let attempt = 1; ; attempt += 1) {
        const fd = new FormData();
        fd.append('upload', upload);
        fd.append('part_number', n);
        fd.append('piece', piece, file.name);
        try {
          // eslint-disable-next-line no-await-in-loop
          await defaultClient.request({
            url: `${base}/piece/`,
            method: 'POST',
            payload: fd,
            options: {
              onUploadProgress: (e) => onProgress(Math.min(1, (sentBefore + Math.min(e.loaded, piece.size)) / (file.size || 1))),
            },
          });
          break;
        } catch (err) {
          // A connection blip retries just this piece; a 400 won't get better.
          if (attempt >= PIECE_TRIES || err?.response?.status === 400) throw err;
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => { setTimeout(resolve, 1000 * attempt); });
        }
      }
    }
    const done = await defaultClient.request({ url: `${base}/finish/`, method: 'POST', payload: { upload, pieces } });
    onProgress(1);
    return done.data.file;
  } catch (err) {
    defaultClient.request({ url: `${base}/abort/`, method: 'POST', payload: { upload } }).catch(() => {});
    throw err;
  }
}

const assetShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  label: PropTypes.string,
  asset_type: PropTypes.string,
  file_url: PropTypes.string,
  headline: PropTypes.string,
  primary_text: PropTypes.string,
  description: PropTypes.string,
  cta_type: PropTypes.string,
  comments: PropTypes.arrayOf(PropTypes.object),
});

/** The client's comments on one ad, with their pins on the ad itself. */
function FeedbackThread({ asset, height }) {
  const [seek, setSeek] = useState(null);
  const comments = asset.comments || [];
  const pins = pinsFromComments(comments);
  const byIndex = Object.fromEntries(pins.map((p) => [p.index, p]));
  return (
    <Stack spacing={1.5}>
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fafafa' }}>
        <PinnableMedia asset={asset} pins={pins} seek={seek} height={height}
          onPinClick={(p) => p.t != null && setSeek({ t: p.t })} />
      </Box>
      <Stack spacing={1} sx={{ maxHeight: 220, overflowY: 'auto' }}>
        {comments.length === 0 && <Typography variant="body2" color="text.secondary">No comments yet.</Typography>}
        {comments.map((c, i) => (
          <Box key={i} sx={{ p: 1.25, borderRadius: 1.5, bgcolor: c.role === 'guru' ? '#f0f7ff' : '#fff8f0' }}>
            <Typography variant="caption" fontWeight={600}>
              {byIndex[i] && <Box component="span" sx={{ color: '#ff680d', mr: 0.75 }}>#{byIndex[i].n}</Box>}
              {byIndex[i]?.t != null && (
                <Box component="span" onClick={() => setSeek({ t: byIndex[i].t })}
                  sx={{ color: '#2563eb', cursor: 'pointer', mr: 0.75 }}>
                  {fmtTime(byIndex[i].t)}
                </Box>
              )}
              {c.author}{c.role === 'guru' ? ' (Lead Gurus)' : ''}
              {c.at && <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: 0.75 }}>{new Date(c.at).toLocaleDateString()}</Box>}
            </Typography>
            {c.text && <Typography variant="body2">{c.text}</Typography>}
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}

FeedbackThread.propTypes = { asset: assetShape.isRequired, height: PropTypes.number };

/** Staff: see exactly where and when the client asked for changes. */
export function CreativeFeedbackDialog({ asset, onClose }) {
  if (!asset) return null;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{asset.label || 'Ad'} — feedback</DialogTitle>
      <DialogContent dividers>
        <FeedbackThread asset={asset} height={420} />
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

CreativeFeedbackDialog.propTypes = { asset: assetShape, onClose: PropTypes.func.isRequired };

/**
 * Staff: replace one ad in place — a new file, new copy, or both. The ad keeps
 * its comments and goes back to pending; it reaches the client on "Send to client".
 */
export function CreativeReplaceDialog({ asset, clientSlug, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [copy, setCopy] = useState({});
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (!asset) return;
    setFile(null);
    setProgress(null);
    setCopy({
      headline: asset.headline || '',
      primary_text: asset.primary_text || '',
      description: asset.description || '',
      cta_type: asset.cta_type || 'LEARN_MORE',
    });
  }, [asset]);

  if (!asset) return null;
  const changed = Object.keys(copy).filter((k) => copy[k] !== (asset[k] || (k === 'cta_type' ? 'LEARN_MORE' : '')));
  const set = (key) => (e) => setCopy((c) => ({ ...c, [key]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      if (file) {
        setProgress(0);
        const token = await uploadCreativeFile(clientSlug, file, setProgress);
        if (token) fd.append('file_token', token);
        else fd.append('file', file);
      }
      changed.forEach((k) => fd.append(k, copy[k]));
      await defaultClient.request({
        url: `app1/creative-review/${clientSlug}/asset/${asset.id}/revision/`,
        method: 'POST',
        payload: fd,
        options: { headers: { 'Content-Type': 'multipart/form-data' } },
      });
      toast.success('Ad replaced — it goes to the client with your next "Send to client"');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not replace the ad');
    } finally {
      setSaving(false);
      setProgress(null);
    }
  };

  let saveLabel = 'Replace ad';
  if (saving) saveLabel = progress !== null && progress < 1 ? `Uploading ${Math.round(progress * 100)}%…` : 'Saving…';

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Replace {asset.label || 'ad'}</DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">What the client said</Typography>
            <FeedbackThread asset={asset} height={260} />
          </Box>
          <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
            <Button variant="outlined" component="label">
              {file ? `New file: ${file.name}` : 'Choose the new file'}
              <input type="file" hidden accept="image/*,video/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </Button>
            <TextField label="Headline" size="small" value={copy.headline || ''} onChange={set('headline')} />
            <TextField label="Primary text" size="small" multiline minRows={3} value={copy.primary_text || ''} onChange={set('primary_text')} />
            <TextField label="Description" size="small" value={copy.description || ''} onChange={set('description')} />
            <TextField select label="CTA" size="small" value={copy.cta_type || 'LEARN_MORE'} onChange={set('cta_type')}>
              {CTAS.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
            </TextField>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || (!file && changed.length === 0)}>
          {saveLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CreativeReplaceDialog.propTypes = {
  asset: assetShape,
  clientSlug: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};
